import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  BarChart3,
  LogOut,
  User,
  Menu,
  X,
  ChevronDown,
  Users,
  Package,
  XCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';

const AdminLayout: React.FC = () => {
  const { user, isFreePlan, logout, canAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    {
      path: '/admin',
      icon: BarChart3,
      label: 'Estatísticas Globais',
      exact: true,
      permission: 'view_dashboard',
      isFreePlan: true,
    },
    {
      path: '/admin/companies',
      icon: LayoutDashboard,
      label: 'Empresas',
      permission: 'view_dashboard',
      isFreePlan: true,
    },
    {
      path: '/admin/all-users',
      icon: Users,
      label: 'Todos os Usuários',
      permission: 'view_dashboard',
      isFreePlan: true,
    },
    {
      path: '/admin/all-leads',
      icon: User,
      label: 'Todos os Leads',
      permission: 'view_dashboard',
      isFreePlan: true,
    },
    {
      path: '/admin/subscription-plans',
      icon: Package,
      label: 'Planos',
      permission: 'view_dashboard',
      isFreePlan: true,
    },
    {
      path: '/admin/cancellation-requests',
      icon: XCircle,
      label: 'Cancelamentos',
      permission: 'view_dashboard',
      isFreePlan: true,
    },
  ];

  // Filtrar itens do menu baseado nas permissões
  const filteredMenuItems = menuItems.filter(item => canAccess(item.permission));

  const isActiveRoute = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:inset-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Painel Administrativo</h1>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          {/* Super Admin Info */}
          <div className="mb-6 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-purple-900">
                  🔑 Painel Super Admin
                </p>
                <p className="text-xs text-purple-600">
                  Visão global do sistema
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            {filteredMenuItems.map((item) => {
              if (item.isFreePlan === false && isFreePlan) {
                return null;
              }

              const Icon = item.icon;
              const isActive = isActiveRoute(item.path, item.exact);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center w-full hover:bg-gray-50 rounded-lg p-2 transition-colors"
            >
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
              </div>
              <div className="ml-3 flex-1 text-left w-20">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.role === 'owner' ? 'Proprietário' :
                    user?.role === 'admin' ? 'Administrador' : 'Usuário'}
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* User dropdown menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                <Link
                  to="/admin/profile"
                  onClick={() => {
                    setShowUserMenu(false);
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="h-4 w-4 mr-3 text-gray-400" />
                  Meu Perfil
                </Link>

                <Link
                  to="/admin/settings"
                  onClick={() => {
                    setShowUserMenu(false);
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="h-4 w-4 mr-3 text-gray-400" />
                  Configurações
                </Link>

                <hr className="my-1 border-gray-200" />
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mr-4 lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;